import React, { FC, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { ReactSVG } from 'react-svg'
import { RootState } from '../../../../../states/containers/store'
import { ResponseMonthlyTeamReport, ResponseMonthlyMemberReport } from '../../../../../states/types/report'
import { nowDate } from '../../../../../utils/date'
import Loading from '../../../common/Loading'
import MonthlyBarGraph from './MonthlyBarGraph'
import MonthlyProcessGraph from './MonthlyProcessGraph'

interface Heatmap {
  type: string
  range: string
  target: string
  id: number
  title: string
  legend: string
  startColor: string
  endColor: string
  minimum: number
  name: string
}

type ScoreColumnName = 'process_score' | 'resource_score' | 'improvement_score' | 'legacy_score' | 'stress_score'

interface Scores {
  label: ScoreColumnName
  current: number
  past: number | null
  company: number
  qasee?: number
  team?: number
  occupation?: number
  distributions: {
    from: number
    to: number
    percent: number
  }[]
}

interface Search {
  startDate: string
  endDate: string
  teamIds: number[]
  teamNames: string[]
  memberIds: number[]
  memberNames: string[]
  keywords: string[]
  serviceNames: string[]
  categoryIds: number[]
  categoryNames: string[]
  efficiencyLevel: string[]
  stressLevel: string[]
  focusLevel: string[]
  sortKey?: string,
  sortType?: string,
}

interface Props {
  target: string
  searchModalOpenCallback: (search: Search) => void,
}

const MonthlyProcess: FC<Props> = (props) => {
  const {
    target,
    searchModalOpenCallback,
  } = props

  const {
    report,
  } = useSelector((state: RootState) => (state))

  const [ruledLineHeight, setRuledLineHeight] = useState<number>(0)

  const [data, setData] = useState<ResponseMonthlyTeamReport | ResponseMonthlyMemberReport>()
  const [scores, setScores] = useState<Scores>()
  const [id, setId] = useState<number>(0)
  const [name, setName] = useState<string>("")

  const subContentsRef = useRef<HTMLDivElement>(null)
  const subContentsLastRef = useRef<HTMLDivElement>(null)

  const maxPersonalized = useMemo(() => {
    if (!data?.process_files?.personalized.files) return 0
    return Math.max(...data.process_files.personalized.files.map((value) => (
      value.use_biz * value.switch_count * (value.use_member_ids?.length || 1) * 1.1
    )) || 0)
  }, [data?.process_files?.personalized.files])

  const maxRpa = useMemo(() => {
    if (!data?.process_files?.rpa.files) return 0
    return Math.max(...data?.process_files?.rpa.files.map((value) => (
      value.use_biz * value.switch_count * (value.use_member_ids?.length || 1) * 1.1
    )) || 0)
  }, [data?.process_files?.rpa.files])

  const maxMismatch = useMemo(() => {
    if (!data?.process_files?.mismatch.files) return 0
    return Math.max(...data?.process_files?.mismatch.files.map((value) => value.use_time)) * 1.1 || 0
  }, [data?.process_files?.mismatch.files])

  useEffect(() => {
    if (!subContentsRef.current?.offsetHeight || !subContentsLastRef.current?.offsetHeight) return
    setRuledLineHeight(subContentsRef.current?.offsetHeight - (subContentsLastRef.current?.offsetHeight / 2) + 40)
  }, [subContentsRef.current?.offsetHeight, subContentsLastRef.current?.offsetHeight])

  useEffect(() => {
    if (!target) return
    if (report.isLoading) return
    if (target === "team") {
      if (!report.monthlyTeamReport) return
      setData(report.monthlyTeamReport)
      setScores(report.monthlyTeamReport.scores?.find((score) => score.label === "process_score"))
      setId(report.monthlyTeamReport.team?.id || 0)
      setName(report.monthlyTeamReport.team?.name || "")
    } else {
      if (!report.monthlyMemberReport) return
      setData(report.monthlyMemberReport)
      setScores(report.monthlyMemberReport.scores?.find((score) => score.label === "process_score"))
      setId(report.monthlyMemberReport.member?.id || 0)
      setName(report.monthlyMemberReport.member?.name || "")
    }
  }, [target, report])

  return (
    <>
      {
        report.isLoading ? (
          <>
            <Loading />
          </>
        ) : (
          <>
            <section id="monthly_process" className="page_contents">
              <div
                className="vertical_ruler"
                style={{
                  height: `${ruledLineHeight}px`,
                }}
              />
              <div id="process" className="content-block monthly">
                <div className="block-detail score">
                  <div className="header_area">
                    <div className="prefix">
                      パフォーマンス
                      <br />
                      【効率度】
                    </div>
                    <div className="title">
                      プロセス指数
                    </div>
                    <div className="point">
                      {scores?.current || 0}
                    </div>
                    <div className="description">
                      属人化やルーティンワーク、ミスマッチなアプリケーションの存在から業務プロセスに対する評価を行なっています。
                    </div>
                  </div>
                  <MonthlyBarGraph
                    target={target}
                    value={{
                      current: scores?.current || 0,
                      past: scores?.past || 0,
                      team: scores?.team,
                      occupation: scores?.occupation,
                      company: scores?.company || 0,
                      qasee: scores?.qasee,
                      distributions: scores?.distributions || [],
                    }}
                  />
                </div>
              </div>
            </section>
            <section ref={subContentsRef}>
              <section className="inner_contents">
                <div className="horizontal_ruler" />
                <section className="page_contents">
                  <div className="content-block">
                    <div className="block-header">
                      <div className="title">
                        {
                          data?.process_files?.personalized.checked && (
                            <ReactSVG
                              src="/static/images/icon/icon-warn.svg"
                              className="icon_warn"
                            />
                          )
                        }
                        属人化傾向にあるファイル
                      </div>
                      <div className="description">
                        使用者が少なく、作業が属人化傾向にあるファイルを抽出しています
                      </div>
                    </div>
                    <div className="block-detail process_wrap">
                      <MonthlyProcessGraph
                        target={target}
                        type="personalized"
                        maxValue={maxPersonalized}
                        files={data?.process_files?.personalized.files || []}
                        startDate={data?.date?.from || nowDate('YYYY-MM-DD')}
                        endDate={data?.date?.to || nowDate('YYYY-MM-DD')}
                        id={id}
                        name={name}
                        searchModalOpenCallback={(search) => searchModalOpenCallback(search)}
                      />
                    </div>
                  </div>
                </section>
              </section>
              <section className="inner_contents">
                <div className="horizontal_ruler" />
                <section className="page_contents">
                  <div className="content-block">
                    <div className="block-header">
                      <div className="title">
                        {
                          data?.process_files?.rpa.checked && (
                            <ReactSVG
                              src="/static/images/icon/icon-warn.svg"
                              className="icon_warn"
                            />
                          )
                        }
                        自動化検討余地のあるファイル
                      </div>
                      <div className="description">
                        多く利用されている、作業の自動化を検討する余地のあるファイルを抽出しています
                      </div>
                    </div>
                    <div className="block-detail process_wrap">
                      <MonthlyProcessGraph
                        target={target}
                        type="rpa"
                        maxValue={maxRpa}
                        files={data?.process_files?.rpa.files || []}
                        startDate={data?.date?.from || nowDate('YYYY-MM-DD')}
                        endDate={data?.date?.to || nowDate('YYYY-MM-DD')}
                        id={id}
                        name={name}
                        searchModalOpenCallback={(search) => searchModalOpenCallback(search)}
                      />
                    </div>
                  </div>
                </section>
              </section>
              <section
                className="inner_contents"
                ref={subContentsLastRef}
              >
                <div className="horizontal_ruler" />
                <section className="page_contents">
                  <div className="content-block">
                    <div className="block-header">
                      <div className="title">
                        {
                          data?.process_files?.mismatch.checked && (
                            <ReactSVG
                              src="/static/images/icon/icon-warn.svg"
                              className="icon_warn"
                            />
                          )
                        }
                        ミスマッチアプリ
                      </div>
                      <div className="description">
                        あまり利用されていない、ミスマッチなアプリを抽出しています
                      </div>
                    </div>
                    <div className="block-detail process_wrap">
                      <MonthlyProcessGraph
                        target={target}
                        type="mismatch"
                        maxValue={maxMismatch}
                        files={data?.process_files?.mismatch.files || []}
                        startDate={data?.date?.from || nowDate('YYYY-MM-DD')}
                        endDate={data?.date?.to || nowDate('YYYY-MM-DD')}
                        id={id}
                        name={name}
                        searchModalOpenCallback={(search) => searchModalOpenCallback(search)}
                      />
                    </div>
                  </div>
                </section>
              </section>
            </section>
          </>
        )
      }
    </>
  )
}

export default MonthlyProcess
